import { z } from 'zod';

const ownerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const storageLocationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const siteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const shipmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const donorSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const sireSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const breedSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const editEmbryoFormSchema = z.object({
  embryoId: z.string().min(1, 'Embryo ID is required'),
  status: z.number().optional(),
  freshFrozen: z.number().optional(),
  owner: ownerSchema
    .or(z.string().uuid())
    .optional()
    .transform((owner) =>
      owner ? (typeof owner === 'string' ? owner : owner.id) : undefined,
    ),
  soldTo: z.string().optional(),
  strawDisclaimer: z.string().optional(),
  strawNumber: z.number().optional(),
  cane: z.string().optional(),
  stage: z.number().optional(),
  grade: z.number().optional(),
  storageLocation: storageLocationSchema
    .or(z.string().uuid())
    .optional()
    .transform((location) =>
      location
        ? typeof location === 'string'
          ? location
          : location.id
        : undefined,
    ),
  site: siteSchema
    .or(z.string().uuid())
    .optional()
    .transform((site) =>
      site ? (typeof site === 'string' ? site : site.id) : undefined,
    ),
  shipment: shipmentSchema
    .or(z.string().uuid())
    .optional()
    .transform((shipment) =>
      shipment
        ? typeof shipment === 'string'
          ? shipment
          : shipment.id
        : undefined,
    ),
  creation: z.date().optional().nullable(),
  stateCode: z.number().optional(),
  statusCode: z.number().optional(),
  lastAct: z.string().optional(),
  donor: donorSchema
    .or(z.string().uuid())
    .optional()
    .transform((donor) =>
      donor ? (typeof donor === 'string' ? donor : donor.id) : undefined,
    ),
  sire: sireSchema
    .or(z.string().uuid())
    .optional()
    .transform((sire) =>
      sire ? (typeof sire === 'string' ? sire : sire.id) : undefined,
    ),
  embTag: z.string().optional(),
  sex: z.number().optional(),
  breed: breedSchema
    .or(z.string().uuid())
    .optional()
    .transform((breed) =>
      breed ? (typeof breed === 'string' ? breed : breed.id) : undefined,
    ),
  freshStrawNo: z.number().optional(),
  freezeMethod: z.string().uuid().optional(),
  freezer: z.string().optional(),
  dtPlus: z.boolean().optional(),
  tt: z.boolean().optional(),
  freezeComment: z.string().optional(),
  export: z.number().optional(),
  freezeTech: z.string().uuid().optional(),
  recoveryFirmCode: z.string().optional(),
  freezeTime: z.string().optional(),
  receivedOn: z.date().optional().nullable(),
  grader7: z.string().uuid().optional(),
  crackedZona: z.boolean().optional(),
});

export type EditEmbryoFormValues = {
  embryoId: string;
  status?: number;
  freshFrozen?: number;
  ownerId?: string;
  soldTo?: string;
  strawDisclaimer?: string;
  strawNumber?: string;
  cane?: string;
  stage?: number;
  grade?: number;
  storageLocationId?: string;
  siteId?: string;
  shipmentId?: string;
  creation?: Date;
  stateCode?: number;
  statusCode?: number;
  lastAct?: string;
  donorId?: string;
  sireId?: string;
  embTag?: string;
  sex?: number;
  breedId?: string;
  freshStrawNo?: number;
  freezeMethodId?: string;
  freezer?: string;
  dtPlus?: boolean;
  tt?: boolean;
  freezeComment?: string;
  export?: number;
  freezeTechId?: string;
  recoveryFirmCode?: string;
  freezeTime?: string;
  receivedOn?: Date;
  grader7Id?: string;
  crackedZona?: boolean;
};

export type EditEmbryoFormInput = {
  embryoId: string;
  status?: number;
  freshFrozen?: number;
  owner?: { id: string; name: string };
  soldTo?: string;
  strawDisclaimer?: string;
  strawNumber?: number;
  cane?: string;
  stage?: number;
  grade?: number;
  storageLocation?: { id: string; name: string };
  site?: { id: string; name: string };
  shipment?: { id: string; name: string };
  creation?: Date;
  stateCode?: number;
  statusCode?: number;
  lastAct?: string;
  donor?: { id: string; name: string };
  sire?: { id: string; name: string };
  embTag?: string;
  sex?: number;
  breed?: { id: string; name: string };
  freshStrawNo?: number;
  freezeMethod?: string;
  freezer?: string;
  dtPlus?: boolean;
  tt?: boolean;
  freezeComment?: string;
  export?: number;
  freezeTech?: string;
  recoveryFirmCode?: string;
  freezeTime?: string;
  receivedOn?: Date;
  grader7?: string;
  crackedZona?: boolean;
};
